import { msg } from "@lingui/core/macro";
import { createSignal, Show } from "solid-js";
import { Eye, Loader2, Save, Sparkles, X } from "@fleet/icons";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, Switch } from "@fleet/ui";
import { api, createApiMutation } from "@/lib/api";
import type { components } from "@cashsync/shared";
import { useI18n } from "@/providers/i18n-provider";

type EmailTemplate = components["schemas"]["EmailTemplate"];

/**
 * Handlebars double braces are SYNTAX the admin must read, not an i18n placeholder.
 * They must travel as the VALUE of an ICU placeholder: if written inside the
 * translatable literal, ICU consumes the content and renders `{}` (an empty
 * instruction), and the `src/__tests__/i18n-placeholder-syntax.test.ts` guard
 * also flags it as the i18next `{{var}}` bug, which is indistinguishable from outside.
 */
// oxlint-disable-next-line lingui/no-unlocalized-strings -- Handlebars syntax, not translatable copy
const HANDLEBARS_SAMPLE = "{{Name}}";

interface Props {
  /** null => create mode. */
  template: EmailTemplate | null;
  onSaved: () => void | Promise<void>;
  onCancel: () => void;
}

/**
 * Email template editor with live preview and AI assistance (campaign #11).
 * The AI is real (Claude via the backend); if the admin has not configured
 * ANTHROPIC_API_KEY, the backend returns 503 and a clear notice is shown.
 */
export function EmailTemplateEditor(props: Props) {
  const { t } = useI18n();
  const isEdit = () => props.template != null;

  // The editor is remounted for each template (Show + a different key), so
  // initializing the signals from props in the constructor is intentional.
  /* oxlint-disable solid/reactivity*/
  const [key, setKey] = createSignal(props.template?.key ?? "");
  const [name, setName] = createSignal(props.template?.name ?? "");
  const [category, setCategory] = createSignal(props.template?.category ?? "marketing");
  const [subject, setSubject] = createSignal(props.template?.subject ?? "");
  const [body, setBody] = createSignal(props.template?.bodyHtml ?? "");
  const [isActive, setIsActive] = createSignal(props.template?.isActive ?? true);
  /* oxlint-enable solid/reactivity*/
  const [aiPrompt, setAiPrompt] = createSignal("");
  const [aiError, setAiError] = createSignal<string | null>(null);
  const [preview, setPreview] = createSignal<{ subject: string; bodyHtml: string } | null>(null);

  const createMutation = createApiMutation(
    async (vars: components["schemas"]["CreateEmailTemplateDto"]) =>
      api.POST("/api/admin/email/templates", { body: vars }),
  );
  const updateMutation = createApiMutation(
    async (vars: components["schemas"]["UpdateEmailTemplateDto"]) =>
      api.POST("/api/admin/email/templates/update", { body: vars }),
  );
  const previewMutation = createApiMutation(
    async (vars: components["schemas"]["PreviewEmailTemplateDto"]) =>
      api.POST("/api/admin/email/templates/preview", { body: vars }),
  );
  const aiMutation = createApiMutation(async (vars: components["schemas"]["AiDraftRequestDto"]) =>
    api.POST("/api/admin/email/templates/ai-draft", { body: vars }),
  );

  const handlePreview = async () => {
    try {
      const r = await previewMutation.mutateAsync({
        subject: subject(),
        bodyHtml: body(),
        context: {
          Name: "Ana",
          Link: "https://cashsync.app/x",
          UnsubscribeLink: "https://cashsync.app/u",
        },
      });
      setPreview({ subject: r.subject, bodyHtml: r.bodyHtml });
    } catch (err) {
      console.error("preview error", err);
    }
  };

  const handleAi = async () => {
    setAiError(null);
    try {
      const draft = await aiMutation.mutateAsync({
        prompt: aiPrompt(),
        category: category(),
        currentSubject: subject() || undefined,
        currentBodyHtml: body() || undefined,
        variables: ["Name", "Link", "UnsubscribeLink"],
      });
      setSubject(draft.subject);
      setBody(draft.bodyHtml);
    } catch (err) {
      // 503 when ANTHROPIC_API_KEY is missing (external AI limit).
      setAiError(
        t(msg`AI assistance is unavailable. Ask the admin to configure ANTHROPIC_API_KEY.`),
      );
      console.error("ai-draft error", err);
    }
  };

  const handleSave = async () => {
    try {
      if (isEdit() && props.template) {
        await updateMutation.mutateAsync({
          id: props.template.id,
          name: name(),
          subject: subject(),
          bodyHtml: body(),
          variables: props.template.variables ?? [],
          isActive: isActive(),
        });
      } else {
        await createMutation.mutateAsync({
          key: key(),
          category: category(),
          name: name(),
          subject: subject(),
          bodyHtml: body(),
          variables: [],
        });
      }
      await props.onSaved();
    } catch (err) {
      console.error("save error", err);
    }
  };

  const inputClass =
    "w-full rounded-lg border border-border bg-field px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary";

  return (
    <Card>
      <CardHeader>
        <CardTitle>{isEdit() ? t(msg`Edit Template`) : t(msg`New Template`)}</CardTitle>
      </CardHeader>
      <CardContent class="space-y-4">
        <Show when={!isEdit()}>
          <div class="grid gap-3 sm:grid-cols-2">
            <label class="block text-sm">
              <span class="mb-1 block text-muted-foreground">{t(msg`Key`)}</span>
              <input
                class={inputClass}
                value={key()}
                onInput={(e) => setKey(e.currentTarget.value)}
              />
            </label>
            <label class="block text-sm">
              <span class="mb-1 block text-muted-foreground">{t(msg`Category`)}</span>
              <select
                class={inputClass}
                value={category()}
                onChange={(e) => setCategory(e.currentTarget.value)}
              >
                <option value="transactional">{t(msg`Transactional`)}</option>
                <option value="marketing">{t(msg`Marketing`)}</option>
                <option value="product">{t(msg`Product`)}</option>
              </select>
            </label>
          </div>
        </Show>

        <label class="block text-sm">
          <span class="mb-1 block text-muted-foreground">{t(msg`Name`)}</span>
          <input
            class={inputClass}
            value={name()}
            onInput={(e) => setName(e.currentTarget.value)}
          />
        </label>

        <label class="block text-sm">
          <span class="mb-1 block text-muted-foreground">{t(msg`Subject`)}</span>
          <input
            class={inputClass}
            value={subject()}
            onInput={(e) => setSubject(e.currentTarget.value)}
          />
        </label>

        <Show when={isEdit()}>
          <div class="flex items-center justify-between rounded-lg border border-border px-3 py-2">
            <span class="text-sm text-muted-foreground">{t(msg`Active`)}</span>
            <Switch checked={isActive()} onCheckedChange={(v) => setIsActive(v)} />
          </div>
        </Show>

        <label class="block text-sm">
          <span class="mb-1 block text-muted-foreground">
            {t(msg`Body (HTML, Handlebars variables like ${HANDLEBARS_SAMPLE})`)}
          </span>
          <textarea
            class={`${inputClass} min-h-40 font-mono`}
            value={body()}
            onInput={(e) => setBody(e.currentTarget.value)}
          />
        </label>

        {/* Asistencia IA */}
        <div class="rounded-lg border border-dashed border-border p-3">
          <div class="mb-2 flex items-center gap-2 text-sm font-medium">
            <Sparkles class="h-4 w-4 text-primary" />
            {t(msg`AI assist`)}
          </div>
          <textarea
            class={`${inputClass} min-h-16`}
            placeholder={t(
              msg`Describe the email you want, e.g. "a friendly welcome email for new users"`,
            )}
            value={aiPrompt()}
            onInput={(e) => setAiPrompt(e.currentTarget.value)}
          />
          <div class="mt-2 flex items-center gap-2">
            <Button
              variant="secondary"
              size="sm"
              class="gap-1"
              disabled={aiMutation.isPending || !aiPrompt()}
              onClick={handleAi}
            >
              <Show when={aiMutation.isPending} fallback={<Sparkles class="h-4 w-4" />}>
                <Loader2 class="h-4 w-4 animate-spin" />
              </Show>
              {t(msg`Generate with AI`)}
            </Button>
            <Show when={aiError()}>
              <span class="text-xs text-destructive">{aiError()}</span>
            </Show>
          </div>
        </div>

        <Show when={preview()}>
          {(p) => (
            <div class="rounded-lg border border-border p-3">
              <p class="mb-2 text-xs font-medium text-muted-foreground">
                {t(msg`Preview`)}: {p().subject}
              </p>
              {/* oxlint-disable-next-line solid/no-innerhtml */}
              <div class="prose prose-sm max-w-none" innerHTML={p().bodyHtml} />
            </div>
          )}
        </Show>

        <div class="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            class="gap-1"
            onClick={handlePreview}
            disabled={previewMutation.isPending}
          >
            <Eye class="h-4 w-4" />
            {t(msg`Preview`)}
          </Button>
          <Button
            size="sm"
            class="gap-1"
            onClick={handleSave}
            disabled={createMutation.isPending || updateMutation.isPending}
          >
            <Save class="h-4 w-4" />
            {t(msg`Save`)}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            class="gap-1"
            onClick={() => {
              props.onCancel();
            }}
          >
            <X class="h-4 w-4" />
            {t(msg`Cancel`)}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
